import { Scrollbar } from "@smartsuite/react-ui/lib/components/utils/Scrollbar/Scrollbar";
import { Preloader } from "@smartsuite/react-ui/lib/components/utils/Preloader/Preloader";
import { useQuery } from "@tanstack/react-query";
import cx from "classnames";
import { jsPDF as JsPDF } from "jspdf";
import { isMatch, isString } from "lodash";
import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "svg2pdf.js";
import {
  deleteDocumentDesign,
  fetchDocumentDesignById,
  fetchMembers,
  saveOrUpdateDocument,
} from "../../api/client";
import { DocumentDesign, Field } from "../../api/types";
import { Canvas } from "../../canvas/Canvas";
import { CanvasManager } from "../../canvas/CanvasManager";
import { getDocumentDesignPaperConfig } from "../../canvas/paper-config";
import {
  addFieldItem,
  addStaticItem,
  duplicateItem,
  getItem,
  hasSelectedField,
  modifyItem,
  removeItem,
} from "../../canvas/state/items";
import { fixState } from "../../canvas/state/state-fixer";
import {
  DocumentDesignItem,
  DocumentDesignItemType,
  DocumentDesignState,
} from "../../canvas/state/types";
import { useRenderContext } from "../../canvas/state/useRenderContext";
import { Actions } from "../Actions/Actions";
import { PageDesignerFooter } from "../PageDesignerFooter/PageDesignerFooter";
import { PageDesignerHeader } from "../PageDesignerHeader/PageDesignerHeader";
import { Paginator } from "../Paginator/Paginator";
import { SearchRecordButton } from "../SearchRecordButton/SearchRecordButton";
import { Sidebar, SidebarOptions } from "../Sidebar/Sidebar";
import { StaticElementCanvas } from "../SidebarElementList/types";
import {
  getOrientedSize,
  PaperOrientation,
  PaperSize,
  paperSizeConfigs,
} from "../paper.constants";
import { SidebarSelectionConfig } from "../SidebarSelectionConfig/SidebarSelectionConfig";
import { SidebarAction } from "../types";
import { getSolutionColorStyles } from "./PageDesigner.helper";
import { getPartialDocument } from "./partial-document";
import { useExitConfirmation } from "./useExitConfirmation";
import { CircleLoaderIcon } from "@smartsuite/react-ui/lib/components/loaders/CircleLoaderIcon/CircleLoaderIcon";

export interface PaddingDirections {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface CanvasPaperConfig {
  height: number;
  width: number;
  orientation: PaperOrientation;
  paddingDirections: PaddingDirections;
  pageBreakMargin: number;
}

export interface PageDesignerProps {
  appId: string;
  documentDesignId: string;
  solutionPrimaryColor?: string;
  onDuplicateDesign: (
    state: DocumentDesignState,
    paperConfig: CanvasPaperConfig,
    name: string,
  ) => void;
}

const PageDesignerRaw: FC<PageDesignerProps> = ({
  appId,
  documentDesignId,
  solutionPrimaryColor,
  onDuplicateDesign,
}) => {
  const [selectedItemId, setSelectedItemId] = useState<string>();
  const [manager, setManager] = useState<CanvasManager>();
  const [recordIndex, setRecordIndex] = useState(0);
  const [canvasPaperConfig, setCanvasPaperConfig] = useState<CanvasPaperConfig>(
    {
      ...getOrientedSize(
        paperSizeConfigs[PaperSize.Letter],
        PaperOrientation.Portrait,
      ),
      orientation: PaperOrientation.Portrait,
      paddingDirections: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
      pageBreakMargin: 0,
    },
  );
  const [documentDesign, setDocumentDesign] = useState<DocumentDesign>();
  const [isPreviewing, setIsPreviewing] = useState<boolean>(false);
  const [unsavedDocumentName, setUnsavedDocumentName] =
    useState<string>("New Document");
  const [activeSidebar, setActiveSidebar] = useState<SidebarOptions>(
    SidebarOptions.fields,
  );
  const [state, setState] = useState<DocumentDesignState>({ items: [] });
  const { Component: ConfirmationDialog, exitWithConfirmation } =
    useExitConfirmation();

  const exit = useCallback(
    (onExit: () => void) => {
      const isNewSateChanged = !documentDesign && !!state.items.length;
      const isPrevSateChanged =
        !!documentDesign &&
        !isMatch(
          documentDesign,
          getPartialDocument(documentDesign, state, canvasPaperConfig),
        );
      const hasChanges = isNewSateChanged || isPrevSateChanged;

      if (!hasChanges) {
        onExit();
        return;
      }

      exitWithConfirmation({
        onSaveChanges: () => {
          saveOrUpdateDocument(
            state,
            canvasPaperConfig,
            appId,
            unsavedDocumentName,
            documentDesign,
          )
            .then((result) => {
              setDocumentDesign(result);
              onExit();
            })
            .catch(console.error);
        },
        onExitWithoutSaving: () => onExit(),
      });
    },
    [
      appId,
      canvasPaperConfig,
      documentDesign,
      exitWithConfirmation,
      state,
      unsavedDocumentName,
    ],
  );

  const context = useRenderContext(
    state,
    appId,
    recordIndex,
    canvasPaperConfig,
  );

  const selectedItem = useMemo(
    () => (selectedItemId ? getItem(state, selectedItemId) : undefined),
    [state, selectedItemId],
  );

  const selectedField = useMemo(() => {
    if (hasSelectedField(selectedItem)) {
      return context.application?.structure.find(
        (field) => field.slug === selectedItem?.field_slug,
      );
    }

    return undefined;
  }, [context.application?.structure, selectedItem]);

  const { data: members } = useQuery({
    queryKey: ["members"],
    queryFn: () => fetchMembers(),
  });

  const sidebarDivRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (documentDesignId && context.application) {
      // TODO: convert to useQuery
      fetchDocumentDesignById(documentDesignId)
        .then((result) => {
          if (result?.canvas_template && context.application) {
            setState(fixState(result.canvas_template, context.application));
          }

          setDocumentDesign(result);
          setCanvasPaperConfig(getDocumentDesignPaperConfig(result));
        })
        .catch(console.error);
    }
  }, [documentDesignId, context.application]);

  useEffect(() => {
    if (sidebarDivRef.current) {
      sidebarDivRef.current.scrollTop = 0;
    }
  }, [selectedItemId]);

  const handleStateChange = useCallback(
    (updatedState: DocumentDesignState) => setState(updatedState),
    [],
  );

  const handleSelectionChange = useCallback(
    (selectedItemId: string | undefined) => {
      setSelectedItemId(selectedItemId);
    },
    [],
  );

  const handleAddField = useCallback(
    (field: Field) => {
      const [newState, id] = addFieldItem(state, field);
      setState(newState);
      setSelectedItemId(id);
    },
    [state],
  );

  const handleAddElement = useCallback(
    (type: StaticElementCanvas) => {
      const [newState, id] = addStaticItem(state, type);
      setState(newState);
      setSelectedItemId(id);
    },
    [state],
  );

  const handlePreviewClick = useCallback(
    () => setIsPreviewing(!isPreviewing),
    [isPreviewing],
  );

  const handlePdf = useCallback(
    (callback: (doc: JsPDF) => void) =>
      manager?.toJsPDF(canvasPaperConfig, callback),
    [canvasPaperConfig, manager],
  );

  const handleExportClick = useCallback(() => {
    handlePdf((doc: JsPDF) => {
      doc.save(`${documentDesign?.name ?? unsavedDocumentName}.pdf`);
    });
  }, [documentDesign?.name, handlePdf, unsavedDocumentName]);

  const handlePrintClick = useCallback(() => {
    handlePdf((doc: JsPDF) => {
      doc.autoPrint();
      const blob = doc.output("blob");
      window.open(window.URL.createObjectURL(blob));
    });
  }, [handlePdf]);

  const handleCanvasPaperConfigUpdate = useCallback(
    (canvasPaperConfig: CanvasPaperConfig) =>
      setCanvasPaperConfig(canvasPaperConfig),
    [],
  );

  const handleDeleteClick = useCallback(
    (): Promise<void> => deleteDocumentDesign(documentDesignId),
    [documentDesignId],
  );

  const handleRenameClick = useCallback(
    (name: string) => {
      if (documentDesign) {
        documentDesign.name = name;
      }
      setUnsavedDocumentName(name);
    },
    [documentDesign],
  );

  const handleChangeSelected = useCallback(
    (modifiedItem: DocumentDesignItem) => {
      setState((curr) => modifyItem(curr, modifiedItem.id, modifiedItem));
    },
    [],
  );

  const handleDuplicate = useCallback(
    (item: DocumentDesignItem) => {
      const [newState, id] = duplicateItem(state, item);
      setState(newState);
      setSelectedItemId(id);
    },
    [state],
  );

  const handleCloseConfig = useCallback(() => {
    setSelectedItemId(undefined);
    manager?.handleSidebarAction(SidebarAction.removeSelection);
  }, [manager]);

  const handleRemove = useCallback((item: DocumentDesignItem) => {
    setState((curr) => removeItem(curr, item.id));
  }, []);

  const handleAction = useCallback(
    (action: SidebarAction) => manager?.handleSidebarAction(action),
    [manager],
  );

  const classes = cx("page-designer", {
    "page-designer--previewing": isPreviewing,
  });

  const style = useMemo(() => {
    if (!solutionPrimaryColor) {
      return undefined;
    }

    return getSolutionColorStyles(`#${solutionPrimaryColor}`);
  }, [solutionPrimaryColor]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const executeMessage = (message: any): void => {
      switch (message.type) {
        case "close-dialog":
          exit(() => window.parent.postMessage("close", "*"));
          break;
        case "sort-change":
          if (selectedItem?.type === DocumentDesignItemType.linkedRecord)
            handleChangeSelected({ ...selectedItem, sort: message.payload });
          break;
        case "filter-change":
          if (selectedItem?.type === DocumentDesignItemType.linkedRecord)
            handleChangeSelected({ ...selectedItem, filter: message.payload });
          break;
        case "record-search-selected":
          setRecordIndex(message.payload);
          break;
      }
    };

    const handleMessage = (event: MessageEvent): void => {
      if (typeof event.data === "object") {
        return;
      }

      try {
        const message = JSON.parse(event.data);
        executeMessage(message);
      } catch (error) {
        // unknown or malformed message, just ignore
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [
    canvasPaperConfig,
    documentDesign,
    selectedItem,
    state,
    exit,
    handleChangeSelected,
  ]);

  const handleDuplicateTemplate = useCallback(() => {
    exit(() => {
      onDuplicateDesign(
        state,
        canvasPaperConfig,
        documentDesign?.name ?? unsavedDocumentName,
      );
    });
  }, [
    canvasPaperConfig,
    documentDesign?.name,
    exit,
    onDuplicateDesign,
    state,
    unsavedDocumentName,
  ]);

  const handleCloseTemplate = useCallback(
    () => exit(() => window.parent.postMessage("close", "*")),
    [exit],
  );

  return (
    <>
      <div className="page-designer-page" style={style}>
        <PageDesignerHeader
          name={documentDesign?.name ?? unsavedDocumentName}
          onRename={handleRenameClick}
          onDelete={handleDeleteClick}
          onDuplicate={handleDuplicateTemplate}
          onClose={handleCloseTemplate}
        />

        <div className={classes}>
          <div className="page-designer__sidebar" ref={sidebarDivRef}>
            {manager && selectedItem ? (
              <SidebarSelectionConfig
                selected={selectedItem}
                selectedField={selectedField}
                context={context}
                state={state}
                onCloseSelectionConfig={handleCloseConfig}
                onRemove={handleRemove}
                onDuplicate={handleDuplicate}
                onChangeSelected={handleChangeSelected}
                onAction={handleAction}
              />
            ) : (
              <Sidebar
                canvasPaperConfig={canvasPaperConfig}
                onCanvasPaperConfigUpdate={handleCanvasPaperConfigUpdate}
                fields={context.application?.structure ?? []}
                activeSidebar={activeSidebar}
                setActiveSidebar={setActiveSidebar}
                onAddField={handleAddField}
                onAddElement={handleAddElement}
              />
            )}
          </div>

          <div className="page-designer__body">
            <div className="page-designer__in">
              <div className="page-designer__header">
                <div className="page-designer__header-record-controls">
                  <Paginator
                    recordIndex={isString(recordIndex) ? -1 : recordIndex}
                    totalRecords={context.totalRecords}
                    onPageChange={setRecordIndex}
                  />
                  <SearchRecordButton
                    applicationId={context?.application?.id}
                    totalRecords={context.totalRecords}
                  />
                  {context.loading && <CircleLoaderIcon />}
                </div>
                <Actions
                  onPreviewClick={handlePreviewClick}
                  onExportClick={handleExportClick}
                  onPrintClick={handlePrintClick}
                />
              </div>

              <Scrollbar classes="page-designer__canvas-scrollbar" showOnHover>
                <Canvas
                  state={state}
                  context={context}
                  isPreviewing={isPreviewing}
                  onStateChange={handleStateChange}
                  onSelectionChange={handleSelectionChange}
                  onReady={setManager}
                  dimensions={canvasPaperConfig}
                />
                {!context.allLinkedRecordsFetched && <Preloader />}
              </Scrollbar>
            </div>
          </div>
        </div>

        <PageDesignerFooter
          lastUpdated={
            documentDesign?.last_updated
              ? {
                  by: members?.get(documentDesign?.last_updated.by) ?? "me",
                  on: new Date(documentDesign?.last_updated.on).toLocaleString(
                    "en-US",
                    {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "numeric",
                    },
                  ),
                }
              : undefined
          }
          onSave={() =>
            saveOrUpdateDocument(
              state,
              canvasPaperConfig,
              appId,
              unsavedDocumentName,
              documentDesign,
            )
              .then(setDocumentDesign)
              .catch(console.error)
          }
          onCancel={() => exit(() => window.parent.postMessage("close", "*"))}
        />
      </div>

      <ConfirmationDialog />
    </>
  );
};

export const PageDesigner = memo(PageDesignerRaw);
